// ========== SIMPLIFIED STATUS MANAGER ==========
// Handles status messages from firmware
// Much simpler than before - just parse and update state

export function createStatusManager(ctx) {
    const {
        state: { properties, isTimeSynced, isRecording, isStreaming },
        download: { handleFtLine, handleFtError },
        deleteOps: { handleDeleteStatus } = {},
        helpers: { extractLabelFromFilename },
        downloads: { rowsPerSec, sdFiles },
        protocol,
        timeSync: { onDevms },
    } = ctx

    // Parse status message from firmware
    const parseStatusDataview = async (dv) => {
        // Decode text from DataView
        let text = ''
        for (let i = 0; i < dv.byteLength; i++) {
            const c = dv.getUint8(i)
            if (c === 0) break
            text += String.fromCharCode(c)
        }

        if (!text) return

        console.log('[BLE] ← STATUS', text)

        // Let download manager handle its messages first
        if (handleFtLine) {
            try {
                if (await handleFtLine(text)) {
                    return
                }
            } catch (error) {
                console.warn('[STATUS] handleFtLine error:', error)
            }
        }

        if (handleDeleteStatus) {
            try {
                if (handleDeleteStatus(text)) {
                    return
                }
            } catch (error) {
                console.warn('[STATUS] handleDeleteStatus error:', error)
            }
        }

        // Parse key=value pairs
        const parseValue = (key) => {
            const match = text.match(new RegExp(`${key}=([^,]+)`))
            return match ? match[1] : null
        }

        // Recording state
        const rec = parseValue('rec')
        if (rec === '1' || parseValue('recording') === 'on') {
            isRecording.value = true
            properties.recordingText = 'ON'
        } else if (rec === '0' || parseValue('recording') === 'off') {
            isRecording.value = false
            properties.recordingText = 'OFF'
        }

        // Streaming state
        const stream = parseValue('stream')
        if (stream === '1' || parseValue('streaming') === 'on') {
            isStreaming.value = true
            properties.streamingText = 'ON'
        } else if (stream === '0' || parseValue('streaming') === 'off') {
            isStreaming.value = false
            properties.streamingText = 'OFF'
        }

        // Time sync state
        const sync = parseValue('sync')
        if (sync === '1' || text === 'time=synced') {
            isTimeSynced.value = true
        } else if (sync === '0') {
            isTimeSynced.value = false
        }

        // Battery
        const bat = parseValue('bat')
        if (bat) {
            properties.battery = parseInt(bat) || 0
        }

        // Firmware / protocol capabilities
        const fw = parseValue('fw')
        if (fw) properties.firmwareVersion = fw

        const proto = parseValue('proto')
        const protoParsed = proto ? parseInt(proto, 10) : null
        if (proto) {
            properties.protocolVersion = Number.isFinite(protoParsed) ? protoParsed : null
        }

        const chunk = parseValue('chunk')
        const chunkParsed = chunk ? parseInt(chunk, 10) : null
        if (chunk) {
            properties.protocolChunk = Number.isFinite(chunkParsed) ? chunkParsed : null
        }

        const crc = parseValue('crc')
        if (crc) properties.protocolCrc = crc

        if (proto || chunk || crc) {
            const expectedVersion = protocol?.version ?? null
            const expectedChunk = protocol?.chunkSize ?? null
            const expectedCrc = protocol?.crc ?? 'ccitt'
            let ok = true

            if (proto && !Number.isFinite(protoParsed)) {
                ok = false
            }
            if (chunk && !Number.isFinite(chunkParsed)) {
                ok = false
            }
            if (expectedVersion !== null && Number.isFinite(protoParsed)) {
                ok = ok && protoParsed === expectedVersion
            }
            if (expectedChunk !== null && Number.isFinite(chunkParsed)) {
                ok = ok && chunkParsed === expectedChunk
            }
            if (expectedCrc && properties.protocolCrc) {
                ok = ok && properties.protocolCrc === expectedCrc
            }

            properties.protocolOk = ok
        }

        // USB charging
        const usb = parseValue('usb')
        if (usb === '1') {
            properties.usbCharging = true
        } else if (usb === '0') {
            properties.usbCharging = false
        }

        // Device label/tag
        const tag = parseValue('tag')
        if (tag) {
            properties.tag = tag === 'UNSET' ? '' : tag
        }

        // Metrics
        const samples = parseValue('samples')
        if (samples) properties.metrics.samples = parseInt(samples) || 0

        const lost = parseValue('lost')
        if (lost) properties.metrics.lost = parseInt(lost) || 0

        const writeErr = parseValue('wErr')
        if (writeErr) properties.metrics.writeErr = parseInt(writeErr) || 0

        const sdErr = parseValue('sdErr')
        if (sdErr) properties.metrics.sdErr = parseInt(sdErr) || 0

        const i2c = parseValue('i2c')
        if (i2c) properties.metrics.i2c = parseInt(i2c) || 0

        const bleDrop = parseValue('ble')
        if (bleDrop) properties.metrics.bleDrop = parseInt(bleDrop) || 0

        const loopMax = parseValue('lMax')
        if (loopMax) properties.metrics.loopMax = parseInt(loopMax) || 0

        const loopAvg = parseValue('lAvg')
        if (loopAvg) properties.metrics.loopAvg = parseInt(loopAvg) || 0

        const minRam = parseValue('minRam')
        if (minRam) properties.metrics.minRam = parseInt(minRam) || null

        const bytesKB = parseValue('bytesKB')
        if (bytesKB) properties.metrics.bytesKB = parseInt(bytesKB) || 0

        // Time sync phase 1 response (ts2 protocol)
        const devms = parseValue('devms')
        if (devms && onDevms) {
            onDevms({
                devms: parseInt(devms),
                t2Epoch: Date.now(),
                p2: typeof performance !== 'undefined' ? performance.now() : null,
            })
        }

        // Rows per second (for duration estimation)
        const rps = parseValue('rowsPerSec')
        if (rps && rowsPerSec) {
            rowsPerSec.value = parseInt(rps) || 100
        }

        // Recording file info
        const recfile = parseValue('recfile')
        if (recfile) {
            properties.currentRecordingFile = recfile

            // Extract tag from filename if present
            const fileTag = extractLabelFromFilename(recfile)
            if (fileTag && !properties.tag) {
                properties.tag = fileTag
            }
        }

        // Error messages
        if (text.startsWith('error=')) {
            const error = text.replace('error=', '')
            console.error('[STATUS] Error from firmware:', error)

            // Handle specific errors
            if (error === 'busy-recording') {
                isRecording.value = true
                properties.recordingText = 'ON'
            } else if (error === 'sd-not-exfat') {
                properties.sdFs = 'fat'
                properties.sdCacheReady = false
            } else if (error === 'time-not-synced') {
                isTimeSynced.value = false
            } else if (error === 'sd-full') {
                properties.sdFreeMB = '0'
            }

            // Let download manager handle its errors
            if (handleFtError) {
                handleFtError(text)
            }
        }

        // Warning messages
        if (text.startsWith('warning=')) {
            const warning = text.replace('warning=', '')
            console.warn('[STATUS] Warning from firmware:', warning)
        }

        // Success messages
        if (text.startsWith('deleted=')) {
            const filename = text.replace('deleted=', '')
            console.log('[STATUS] File deleted:', filename)

            // Remove from sdFiles
            if (sdFiles?.value) {
                const idx = sdFiles.value.findIndex((f) => f.name === filename)
                if (idx >= 0) {
                    sdFiles.value.splice(idx, 1)
                }
            }
        }
    }

    return {
        parseStatusDataview,
    }
}
